// ─── clientAppOptions.js ─────────────────────────────────────────────
// Le suivi du poids et les photos de repas sont des colonnes de la fiche
// cliente, côté app. Cette fiche n'existe qu'à partir de la publication :
// avant, il n'y a rien à cocher. Anissa devait donc publier d'abord, puis
// revenir en arrière pour régler, ce que personne ne devine.
//
// Elle pose désormais son choix quand elle veut. Le SaaS le retient, et il est
// appliqué à la création de l'espace, en une fois.
//
// ⚠ Le choix est rangé dans une colonne dédiée de `clients`, jamais dans
// `editorial_overrides` : ce champ entre dans l'empreinte scellée du livrable
// et y ajouter une clé invaliderait la preuve de publication.

export const CLIENT_APP_OPTION_KEYS = Object.freeze([
  'weight_tracking_enabled',
  'weight_visible_to_client',
  'meal_photos_enabled',
]);

/** Lecture tolérante : une option absente vaut « non activée ». */
export function readClientAppOptions(client) {
  const brut = client?.clientAppOptions || client?.client_app_options || null;
  const options = {};
  for (const cle of CLIENT_APP_OPTION_KEYS) {
    options[cle] = brut?.[cle] === true;
  }
  return Object.freeze(options);
}

/** Y a-t-il au moins un choix posé, en attente de publication ? */
export function hasPendingClientAppOptions(client) {
  const options = readClientAppOptions(client);
  return CLIENT_APP_OPTION_KEYS.some((cle) => options[cle]);
}

/**
 * Applique un choix, en gardant les deux réglages du poids cohérents :
 * autoriser la saisie cliente sans activer le suivi n'a aucun sens, et couper
 * le suivi doit couper la saisie.
 */
export function withClientAppOption(client, key, value) {
  if (!CLIENT_APP_OPTION_KEYS.includes(key)) {
    throw new Error('Option inconnue pour l’espace cliente.');
  }
  const options = { ...readClientAppOptions(client), [key]: value === true };
  if (key === 'weight_tracking_enabled' && value !== true) {
    options.weight_visible_to_client = false;
  }
  if (key === 'weight_visible_to_client' && value === true) {
    options.weight_tracking_enabled = true;
  }
  return { ...client, clientAppOptions: options };
}

/** Le suivi du poids se pose d'un seul geste : collecte + saisie cliente. */
export function withBothWeightOptions(client, value) {
  const actif = value === true;
  return {
    ...client,
    clientAppOptions: {
      ...readClientAppOptions(client),
      weight_tracking_enabled: actif,
      weight_visible_to_client: actif,
    },
  };
}

/**
 * Après une publication réussie, pousse le choix retenu vers l'espace qui
 * vient d'être créé. Non bloquant par construction : la publication a abouti,
 * une option non appliquée se règle d'un clic et ne doit jamais faire croire
 * que la publication a échoué.
 *
 * @returns {Promise<{applied: boolean, options: object, error: string|null}>}
 */
export async function applyPendingClientAppOptions(client, { update } = {}) {
  const options = readClientAppOptions(client);
  const demandees = CLIENT_APP_OPTION_KEYS.filter((cle) => options[cle]);
  if (demandees.length === 0) {
    return Object.freeze({ applied: false, options, error: null });
  }
  if (typeof update !== 'function') {
    return Object.freeze({ applied: false, options, error: 'Mise à jour indisponible.' });
  }
  try {
    await update(client, Object.fromEntries(
      CLIENT_APP_OPTION_KEYS.map((cle) => [cle, options[cle]]),
    ));
    return Object.freeze({ applied: true, options, error: null });
  } catch (error) {
    return Object.freeze({
      applied: false,
      options,
      error: error?.message || 'Les options n’ont pas pu être appliquées.',
    });
  }
}
